import { customerStory as cs, cta } from '../content';
import { usePageMeta, Swipe, Button, Arrow, SmartLink } from '../ui';
import { BookingWidget } from '../booking/BookingWidget';
import { ShareButtons } from './industries/Share';
import { PackingSlip, CasePhone } from './customers/Visuals';
import { HERO_CSS } from './industries/Hero';
import { MakerPlate, type PlateSpec } from '../ui/brandcode';

// Illustrative only: no customer name, order or part data.
const SLIP: PlateSpec = {
  finish: 'slip',
  maker: 'Delivery note',
  kind: 'Despatch 24-1182',
  mark: 'D',
  accent: '#1F3B57',
  rows: [
    ['Order', 'SO-24-1182'],
    ['Your PO', 'PO-77310'],
    ['Lines', '3'],
  ],
  serialKey: 'Order',
  meet: 'See what shipped',
};

const FLOW = [
  { k: 'Scan', d: 'Their customer scans the code on the packing slip.' },
  { k: 'See', d: 'Exactly what shipped, line by line.' },
  { k: 'Report', d: 'If anything is not right, in a minute, with photos.' },
  { k: 'Resolve', d: 'A case their team can close with the facts in hand.' },
];

// Contracted, not live yet. Plan in general terms only. No dates, no names.
const NEXT = [
  { t: 'Scannable packing slips', d: 'Straight from the ERP, with no change to despatch.' },
  { t: 'A first passport on a real part', d: 'Its own code on the part, fed from the same ERP data.' },
  { t: 'Passport data from the ERP', d: 'So nobody types it twice.' },
  { t: 'A beta with their own customers', d: 'Tested end to end on real deliveries.' },
  { t: 'The first company in the group live', d: 'Then the next, at the pace the business sets.' },
];

// Case-study fact strip. Anonymised: no country, headcount or ERP brand until the customer agrees.
const FACTS = [
  { k: 'Industry', v: 'Precision engineering' },
  { k: 'Region', v: 'Europe' },
  { k: 'Status', v: 'Contracted' },
  { k: 'Starts with', v: 'Packing slips' },
  { k: 'Then', v: 'Digital Product Passport' },
];

const TOC = [
  { id: 'why', t: 'Why they chose it' },
  { id: 'outcome', t: 'The approach' },
  { id: 'process', t: 'How it works' },
];

// Honest wording: no passport is required for their parts yet (iron and steel act not adopted).
const OUTCOMES = [
  { v: 'Every slip', d: 'Its own code, straight from the ERP' },
  { v: 'No change', d: 'To how they pick, pack and despatch' },
  { v: 'Then each part', d: 'Its own code, carrying the Digital Product Passport' },
];

const PARTNER_GET = [
  'One product line live in weeks, connected to your ERP',
  'Real units, used by your dealers or customers',
  'A review at 90 days against your measures',
  'Your data and links stay yours',
];
const PARTNER_GIVE = ['The product line and the scope', 'What success looks like, in your numbers', 'Who owns it on your side'];

export default function Customers() {
  usePageMeta(
    'Customer story: a leading European manufacturer',
    'A leading European manufacturer has signed with BrandedMark. They start with scannable packing slips from their ERP, then the Digital Product Passport.'
  );

  return (
    <div className="cs-page">
      {/* Hero: house style, a real photo and the delivery label with its code */}
      <section className="indh is-right">
        <style>{HERO_CSS}</style>
        <div className="wrap indh-grid">
          <div className="indh-copy">
            <div className="indh-kick">
              <span className="bar" aria-hidden="true" />
              <span>{cs.who}</span>
            </div>
            <h1 className="t-h1 indh-h1 cs-h1">
              Everything that leaves the factory, <Swipe draw>connected.</Swipe>
            </h1>
            <p className="t-lede indh-sub">
              Packing slips first, then the passport.
            </p>
            <div className="indh-cta">
              <Button href={`${cta.primaryHref}?src=customers`}>
                {cta.primary} <Arrow />
              </Button>
              <a href="#pilot" className="indh-quiet">
                or pilot one product line
              </a>
            </div>
            <ul className="indh-proof">
              <li>Works with your ERP</li>
              <li>GDPR</li>
            </ul>
          </div>
          <div className="indh-vis">
            <img src="/images/v2/gen/precision-packing.jpg" alt="Gloved hands packing machined flanges into a wooden case, the delivery paperwork tucked in the lid" width={1536} height={1024} className="indh-img" style={{ objectPosition: '62% 40%' }} />
            <div className="indh-scrim" aria-hidden="true" />
            <div className="indh-plate" role="img" aria-label="An illustrative delivery label with a scannable code printed into it">
              <MakerPlate p={SLIP} />
            </div>
          </div>
        </div>
      </section>

      {/* Fact strip: the case-study panel */}
      <section className="border-b border-bm-line">
        <div className="wrap py-8 sm:py-10">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-5">
            {FACTS.map((f) => (
              <div key={f.k}>
                <dt className="text-[14px] text-bm-muted">{f.k}</dt>
                <dd className="mt-1 text-[16px] font-semibold leading-snug text-bm-indigo">{f.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Story: problem, outcome, new process */}
      <section>
        <div className="wrap section grid gap-14 lg:grid-cols-[240px_1fr] lg:gap-20">
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <nav aria-label="In this story">
              <div className="text-[14px] font-semibold text-bm-indigo">In this story</div>
              <ol className="mt-4 space-y-3 border-l border-bm-line pl-4 text-[15px]">
                {TOC.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="text-bm-muted hover:text-bm-indigo">
                      {s.t}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
            <ShareButtons className="mt-8" subject="Customer story: a leading European manufacturer on BrandedMark" body={(url) => `Hi,\n\nShort read. A leading European manufacturer is starting with scannable packing slips, taking the automation they already run in the factory out to their customers. The passport comes next, with a code on each part.\n\n${url}\n`} label="Send this story" />
          </aside>

          <article className="max-w-[720px]">
            {/* Customer quote goes here once they agree to be quoted, with name and role. Never invent one. */}

            <div id="why" className="scroll-mt-28">
              <div className="t-eyebrow">Why they chose it</div>
              <h2 className="t-h2 mt-4">The same standard after it ships.</h2>
              <p className="mt-6 text-[18px] leading-[1.7] text-bm-ink">
                {cs.descriptor}. Quality is built into how they work, and they automate wherever it helps. A code on every delivery carries that care past the factory gate: their customer scans it and sees exactly what shipped. It is the first step towards a Digital Product Passport for every part.
              </p>
              <p className="mt-5 text-[18px] leading-[1.7] text-bm-ink">
                And if anything is ever not right, they want to be the easiest company to work with.
              </p>
            </div>

            <div id="outcome" className="mt-20 scroll-mt-28">
              <div className="t-eyebrow">The approach</div>
              <h2 className="t-h2 mt-4">Packing slips first. Then the passport.</h2>
              <dl className="mt-10 grid gap-px overflow-hidden rounded-2xl bg-bm-line ring-1 ring-bm-line sm:grid-cols-3">
                {OUTCOMES.map((o) => (
                  <div key={o.v} className="bg-white p-6">
                    <dt className="text-[clamp(26px,3vw,34px)] font-extrabold leading-none tracking-[-0.03em] text-bm-indigo">{o.v}</dt>
                    <dd className="mt-3 text-[15px] leading-snug text-bm-muted">{o.d}</dd>
                  </div>
                ))}
              </dl>
              <ul className="mt-10 space-y-6">
                {cs.chapters.map((c) => (
                  <li key={c.k}>
                    <h3 className="text-[20px] font-bold tracking-[-0.02em] text-bm-indigo">{c.t}</h3>
                    <p className="mt-1.5 text-[17px] leading-relaxed text-bm-ink">{c.d}</p>
                  </li>
                ))}
              </ul>
              <div className="my-14 border-l-[5px] pl-6 sm:pl-8" style={{ borderImage: 'var(--bm-gradient) 1' }}>
                <p className="text-[clamp(26px,3vw,38px)] font-extrabold leading-[1.1] tracking-[-0.03em] text-bm-indigo">
                  Their automation already reaches the despatch bay. The code on the packing slip takes it to their customer's door.
                </p>
              </div>
            </div>

            <div id="process" className="scroll-mt-28">
              <div className="t-eyebrow">How it works</div>
              <h2 className="t-h2 mt-4">From a scan to the facts.</h2>
              <p className="mt-6 text-[18px] leading-[1.7] text-bm-ink">
                A code on every packing slip from their ERP, one for each shipment. Their customer sees their brand, with no app and no login.
              </p>
              <ol className="mt-10 grid gap-px overflow-hidden rounded-2xl bg-bm-line ring-1 ring-bm-line sm:grid-cols-4">
                {FLOW.map((f, i) => (
                  <li key={f.k} className="bg-white p-5">
                    <div className="text-[14px] font-semibold text-bm-emerald">{i + 1}</div>
                    <div className="mt-2 text-[18px] font-bold text-bm-indigo">{f.k}</div>
                    <p className="mt-1.5 text-[14.5px] leading-snug text-bm-muted">{f.d}</p>
                  </li>
                ))}
              </ol>
              <figure className="relative mx-auto mt-12 hidden w-full max-w-[560px] sm:block sm:pb-6">
                <div className="absolute inset-x-[4%] bottom-[6%] top-[12%] hidden rounded-[28px] bg-bm-alt sm:block" aria-hidden="true" />
                <div className="relative w-full sm:w-[84%]">
                  <PackingSlip />
                </div>
                <div className="relative -mt-52 ml-auto hidden w-[280px] origin-top-right scale-[.9] sm:block">
                  <CasePhone />
                </div>
              </figure>
            </div>
          </article>
        </div>
      </section>

      {/* What's next */}
      <section>
        <div className="wrap section grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
          <div>
            <span className="bar" />
            <h2 className="t-h2 max-w-[12ch]">The path they chose.</h2>
          </div>
          <ol className="relative ml-2 border-l border-bm-line pl-8 lg:ml-0">
            {NEXT.map((n, i) => (
              <li key={n.t} className="relative pb-10 last:pb-0">
                <span className={`absolute -left-[39px] top-1 grid h-[14px] w-[14px] place-items-center rounded-full ring-4 ring-white ${i === 0 ? 'bg-bm-green' : 'bg-white ring-offset-0'} `} style={i === 0 ? undefined : { boxShadow: 'inset 0 0 0 2px #241E61, 0 0 0 4px #fff' }} />
                <div className="text-[14px] font-medium text-bm-muted">Step {i + 1}</div>
                <h3 className="mt-1 text-[21px] font-bold tracking-[-0.02em] text-bm-indigo">{n.t}</h3>
                <p className="mt-1.5 text-[16px] text-bm-muted">{n.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Pilot */}
      <section id="pilot" className="bg-bm-alt">
        <div className="wrap section">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-end">
            <div>
              <h2 className="t-h2 max-w-[15ch]">
                Start with one line. <Swipe>Decide at 90 days.</Swipe>
              </h2>
            </div>
            <p className="t-lede max-w-[46ch] lg:justify-self-end">
              Outcomes agreed before we start. If it earns its place, roll it out across the range.
            </p>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-[1.2fr_1fr]">
            <div className="frame">
              <div className="p-7 sm:p-9">
                <h3 className="t-h3">What you get</h3>
                <ul className="mt-6 space-y-4">
                  {PARTNER_GET.map((g) => (
                    <li key={g} className="flex gap-3 text-[16.5px] text-bm-ink">
                      <svg className="mt-1 shrink-0" width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="m4.5 10.5 3.5 3.5 7.5-8" stroke="#2F9E58" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {g}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="rounded-[22px] bg-white p-7 ring-1 ring-bm-line sm:p-9">
              <h3 className="t-h3">What we agree up front</h3>
              <ul className="mt-6 space-y-4">
                {PARTNER_GIVE.map((g) => (
                  <li key={g} className="flex gap-3 text-[16.5px] text-bm-ink">
                    <span className="mt-[11px] h-[3px] w-3 shrink-0 rounded-full bg-bm-indigo" aria-hidden="true" />
                    {g}
                  </li>
                ))}
              </ul>
              <SmartLink href="/pricing" className="mt-8 inline-flex items-center gap-2 text-[15px] font-semibold text-bm-indigo hover:text-bm-emerald">
                Start with one product line <Arrow />
              </SmartLink>
            </div>
          </div>
        </div>
      </section>

      {/* Book */}
      <section className="on-dark bg-bm-deep">
        <div className="wrap py-20 sm:py-28">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-end">
            <h2 className="t-h2 max-w-[16ch]">
              Let&rsquo;s <Swipe>talk</Swipe>.
            </h2>
            <p className="t-lede max-w-[46ch] lg:justify-self-end">Bring one product, or one delivery, and get an honest view of fit, effort and cost.</p>
          </div>
          <div className="mt-12 min-w-0">
            <BookingWidget compact source="customers" />
          </div>
        </div>
      </section>

      <style>{`
        @media (min-width: 1024px) { .v2 .indh .cs-h1 { max-width: 15ch; } }
      `}</style>
    </div>
  );
}
